import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getToken, api } from './utils/api';
import Sidebar from './components/Sidebar';

import Login        from './pages/Login';
import Setup        from './pages/Setup';
import Dashboard    from './pages/Dashboard';
import Clients      from './pages/Clients';
import ClientProfile from './pages/ClientProfile';
import Vendors      from './pages/Vendors';
import VendorProfile from './pages/VendorProfile';
import Projects     from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Subscriptions from './pages/Subscriptions';
import Reports      from './pages/Reports';
import Settings     from './pages/Settings';

function AppLayout({ children }) {
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="app-main">
        {children}
      </div>
    </div>
  );
}

function ProtectedRoute({ children, isAuth }) {
  if (!isAuth) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const [isAuth, setIsAuth]           = useState(!!getToken());
  const [setupComplete, setSetupComplete] = useState(null); // null = loading
  const [checking, setChecking]       = useState(true);

  useEffect(() => {
    api.get('/config')
      .then(data => {
        setSetupComplete(data.setupComplete);
        setChecking(false);
      })
      .catch(() => {
        setSetupComplete(false);
        setChecking(false);
      });
  }, []);

  if (checking) {
    return (
      <div style={{
        height: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-light)',
        fontSize: 13
      }}>
        Loading…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>

        {/* Public — Login */}
        <Route
          path="/"
          element={
            !setupComplete
              ? <Navigate to="/setup" replace />
              : isAuth
              ? <Navigate to="/dashboard" replace />
              : <Login onLogin={() => setIsAuth(true)} />
          }
        />

        {/* Public — Setup Wizard */}
        <Route
          path="/setup"
          element={
            setupComplete
              ? <Navigate to="/" replace />
              : <Setup onComplete={() => setSetupComplete(true)} />
          }
        />

        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Dashboard /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/clients" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Clients /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/clients/:id" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><ClientProfile /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/vendors" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Vendors /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/vendors/:id" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><VendorProfile /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/projects" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Projects /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/projects/:id" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><ProjectDetail /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/subscriptions" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Subscriptions /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/reports" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Reports /></AppLayout>
          </ProtectedRoute>
        }/>

        <Route path="/settings" element={
          <ProtectedRoute isAuth={isAuth}>
            <AppLayout><Settings /></AppLayout>
          </ProtectedRoute>
        }/>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </BrowserRouter>
  );
}
